import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";
import { convexQuery, useConvexMutation } from "@convex-dev/react-query";

import { Button } from "@/components/ui/button";
import { Doc } from "@cvx/_generated/dataModel";
import { MessageSquare } from "lucide-react";
import StatusBadge from "@/components/Generic/StatusBadge";
import { Textarea } from "@/components/ui/textarea";
import { VideoDiscussionThread } from "@/components/Generic/VideoDiscussionThread";
import { VideoEmbed } from "@/components/ui/video-embed";
import { api } from "@cvx/_generated/api";
import { format } from "date-fns";
import { useQuery } from "@tanstack/react-query";
import { useState, useMemo } from "react";
import { useToast } from "@/components/ui/use-toast";
import { TEAM_USER_IDS } from "@/constants/userIds";

interface ContestDialogProps {
  video: Doc<"contractVideos">;
  onContestSubmitted: () => void;
}

export const ContestDialog = ({
  video,
  onContestSubmitted,
}: ContestDialogProps) => {
  const { toast } = useToast();
  const [isOpen, setIsOpen] = useState(false);
  const [contestMessage, setContestMessage] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);

  const contestMutation = useConvexMutation(
    api.videoReview.contestVideoRejection,
  );

  // Fetch all discussions for this video
  const { data: discussions, refetch: refetchDiscussions } = useQuery({
    ...convexQuery(api.videoReview.getVideoDiscussions, {
      contractVideoId: video._id,
    }),
    enabled: isOpen,
  });

  // Get current user for filtering
  const { data: user } = useQuery(convexQuery(api.app.getCurrentUser, {}));

  // Filter discussions based on user role and teamVideoReviewRequired
  const filteredDiscussions = useMemo(() => {
    if (!discussions || !user) return discussions;

    const isTeamMember = TEAM_USER_IDS.includes(user._id);

    // Team members always see everything
    if (isTeamMember) {
      return discussions;
    }

    // For brand accounts, check if teamVideoReviewRequired is enabled
    // This filtering is now handled on the backend, so we just return the discussions
    return discussions;
  }, [discussions, user]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!contestMessage.trim()) {
      toast({
        title: "Contest message required",
        description: "Please provide a reason for contesting this decision",
        variant: "destructive",
      });
      return;
    }

    try {
      setIsSubmitting(true);
      await contestMutation({
        contractVideoId: video._id,
        message: contestMessage.trim(),
      });

      toast({
        title: "Contest submitted",
        description:
          "Your response has been added to the discussion. The team will review your feedback.",
      });

      setIsOpen(false);
      setContestMessage("");
      refetchDiscussions();
      onContestSubmitted();
    } catch (error) {
      console.error("Error contesting decision:", error);
      toast({
        title: "Error submitting contest",
        description:
          error instanceof Error ? error.message : "An error occurred",
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!video.status) return null;

  return (
    <Sheet open={isOpen} onOpenChange={setIsOpen}>
      <SheetTrigger asChild>
        <Button variant="outline" size="sm">
          📝 Contest
        </Button>
      </SheetTrigger>
      <SheetContent className="w-[90vw] max-w-[1800px] sm:max-w-[1800px] p-0">
        <div className="h-full overflow-hidden">
          <div className="grid grid-cols-[1.2fr,0.8fr] gap-0 h-full">
            {/* Left Side - Video Preview (Full Height) */}
            <div className="bg-black flex items-center justify-center h-screen p-4">
              {video.url && (
                <VideoEmbed
                  url={video.url}
                  platform={video.platform}
                  className="w-full h-full max-h-[90vh]"
                />
              )}
            </div>

            {/* Right Side - Information, Discussion, and Contest Form */}
            <div className="overflow-y-auto p-6 space-y-6 h-screen">
              {/* Header */}
              <div className="pb-4 border-b border-border">
                <h3 className="font-medium text-primary text-lg">
                  Contest Video Rejection
                </h3>
                <p className="text-sm text-muted-foreground mt-1">
                  Review the feedback and explain why you believe this video
                  should be approved.
                </p>
                {video.title && (
                  <p className="text-sm text-muted-foreground mt-1">
                    {video.title}
                  </p>
                )}
              </div>

              {/* Video Information */}
              <div className="space-y-4">
                <h3 className="font-medium text-primary text-lg">
                  Information
                </h3>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="text-sm font-medium text-primary/60">
                      Video Status
                    </label>
                    <div className="mt-1">
                      <StatusBadge status={video.status} />
                    </div>
                  </div>
                  {video.platform && (
                    <div>
                      <label className="text-sm font-medium text-primary/60">
                        Platform
                      </label>
                      <p className="mt-1 text-sm text-primary capitalize">
                        {video.platform}
                      </p>
                    </div>
                  )}
                </div>

                {video._creationTime && (
                  <div>
                    <label className="text-sm font-medium text-primary/60">
                      Submitted
                    </label>
                    <p className="mt-1 text-sm text-primary">
                      {format(
                        new Date(video._creationTime),
                        "MMM dd, yyyy 'at' HH:mm",
                      )}
                    </p>
                  </div>
                )}

                {video.postDate && (
                  <div>
                    <label className="text-sm font-medium text-primary/60">
                      Post Date
                    </label>
                    <p className="mt-1 text-sm text-primary">
                      {format(new Date(video.postDate), "MMM dd, yyyy")}
                    </p>
                  </div>
                )}

                {(video.views ||
                  video.likes ||
                  video.comments ||
                  video.shares) && (
                  <div>
                    <label className="text-sm font-medium text-primary/60">
                      Metrics
                    </label>
                    <div className="mt-1 flex gap-4 text-sm text-primary">
                      {video.views !== undefined && (
                        <span>👁️ {video.views.toLocaleString()} views</span>
                      )}
                      {video.likes !== undefined && (
                        <span>❤️ {video.likes.toLocaleString()}</span>
                      )}
                      {video.comments !== undefined && (
                        <span>💬 {video.comments.toLocaleString()}</span>
                      )}
                      {video.shares !== undefined && (
                        <span>🔁 {video.shares.toLocaleString()}</span>
                      )}
                    </div>
                  </div>
                )}
              </div>

              {/* Discussion Thread */}
              <div className="pt-4 border-t border-border">
                <div className="flex items-center gap-2 mb-4">
                  <MessageSquare className="h-4 w-4" />
                  <h3 className="font-medium text-primary">
                    Feedback & Discussion
                  </h3>
                </div>

                {/* Discussion Messages */}
                <div className="space-y-3 max-h-96 overflow-y-auto mb-4">
                  <VideoDiscussionThread discussions={filteredDiscussions} />
                </div>
              </div>

              {/* Contest Form */}
              <div className="space-y-4 pt-4 border-t border-border">
                <h3 className="font-medium text-primary">Your Response</h3>

                <form onSubmit={handleSubmit} className="space-y-4">
                  <div>
                    <label
                      htmlFor="contestMessage"
                      className="text-sm font-medium text-primary"
                    >
                      Contest Message
                    </label>
                    <Textarea
                      id="contestMessage"
                      value={contestMessage}
                      onChange={(e) => setContestMessage(e.target.value)}
                      placeholder="Explain why you believe this video meets the requirements and should be approved..."
                      className="mt-1"
                      rows={5}
                      required
                    />
                    <p className="text-xs text-muted-foreground mt-1">
                      This will be added to the discussion thread. The team can
                      respond and you can continue the conversation.
                    </p>
                  </div>

                  <div className="flex gap-2 pt-4">
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() => setIsOpen(false)}
                      disabled={isSubmitting}
                      className="flex-1"
                    >
                      Cancel
                    </Button>
                    <Button
                      type="submit"
                      disabled={!contestMessage.trim() || isSubmitting}
                      className="flex-1"
                    >
                      {isSubmitting ? "Submitting..." : "Submit Contest"}
                    </Button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        </div>
      </SheetContent>
    </Sheet>
  );
};
